import _filter from 'lodash/filter';
import _flatMap from 'lodash/flatMap';
import _inRange from 'lodash/inRange';
import _isEmpty from 'lodash/isEmpty';
import _isEqual from 'lodash/isEqual';
import _last from 'lodash/last';
import _map from 'lodash/map';
import _reduce from 'lodash/reduce';
import _uniqWith from 'lodash/uniqWith';
import _some from 'lodash/some';
import _first from 'lodash/first';
import _groupBy from 'lodash/groupBy';
import { splitIntoGroups, getSideName } from '../Visualizer/utils/index';
import { _initialState } from '../../store/compatibility/_initialState';

const FALLBACK_CHOICE_INFO = { span: [0, 0, 0], productType: null };

/**
 * Calculate the total height of a group of products.
 * @param {Array} group - Array of products.
 * @returns {number} - Total height.
 */
const sumHeights = (group) => _reduce(group, (sum, product) => {
  const { span: [,, height] } = product;
  return sum + height;
}, 0);

const sumHeightsProducts = (group) => _reduce(group, (sum, product) => {
  const { Origin: [,, height] } = product;
  return sum + height;
}, 0);

/**
 * Calculate the total area of items based on their spans.
 * @param {Array} items - Array of items.
 * @param {Object} choicesInfo - Mapping of choice IDs to their span information.
 * @returns {number} - Total area.
 */
const sumArea = (items, choicesInfo) => _reduce(items, (sum, item) => {
  const { choiceId } = item;
  const { span: [rowSpan, colSpan] } = choicesInfo[choiceId];
  return sum + (rowSpan * colSpan);
}, 0);

/**
 * Get the base row and column span for a choice.
 * @param {Object} choiceInfo - Information about the choice.
 * @param {Object} baseMatrix - Matrix dimensions.
 * @returns {Array} - [rowSpan, colSpan].
 */
const getBaseRowColSpan = (choiceInfo, baseMatrix) => {
  if (!choiceInfo?.productType) {
    return [0, 0];
  }
  if (choiceInfo?.productType === 'Top') {
    const [productRowSpan] = choiceInfo?.span;
    return [
      (baseMatrix?.rows / productRowSpan) || 0,
      baseMatrix?.columns || 0];
  }
  const [baseRowSpan, baseColSpan] = choiceInfo?.span || [0, 0];
  return [baseRowSpan, baseColSpan];
};

/**
 * Map products to their valid segments.
 * @param {Object} matrix - Matrix dimensions.
 * @param {Array} products - Array of products.
 * @param {Object} choicesInfo - Mapping of choice IDs to their span information.
 * @returns {Array} - Array of valid product segments.
 */
const mapChoiceSegments = (matrix, products, choicesInfo) => _map(products, (product) => {
  const { rows, columns } = matrix;
  const segments = matrix?.segments || [{ Start: [0, 0], End: [rows, columns] }];
  const productSegments = _map(segments, (segment, index) => {
    const { Start: [startRow, startCol], End: [endRow, endCol] } = segment;
    const { Origin: [productRow, productCol], choiceId, Sku } = product;
    const { span: [productRowSpan, productColSpan, productHeightSpan], productType } = choicesInfo?.[choiceId]
      || FALLBACK_CHOICE_INFO;

    const fitsInRows = _inRange(productRow, startRow, endRow);
    const fitsInCols = _inRange(productCol, startCol, endCol);
    const spanInRows = Number(productRowSpan) <= (endRow + 1);
    const spanInCols = Number(productColSpan) <= (endCol + 1);
    const fitsBase = Number(productRowSpan) === rows && Number(productColSpan) === columns;

    const compatible = fitsInRows && fitsInCols && spanInRows && spanInCols;

    const moveRightCompanion = [startRow, startCol + Number(productColSpan)];
    const moveLeftCompanion = [endRow, endCol - Number(productColSpan)];

    return ({
      segment: {
        parent: {
          start: compatible
            ? [startRow, startCol]
            : [rows - Number(productRowSpan), columns - Number(productColSpan)],
          end: compatible
            ? [endRow, endCol]
            : [rows - 1, columns - 1],
          child: {
            start: [productRow, productCol],
            end: [productRow + Number(productRowSpan) - 1, productCol + (Number(productColSpan - 1))],
            companion: (Number(productRowSpan) === rows || Number(productColSpan) === columns)
              ? false
              : {
                start: productCol === 0
                  ? moveRightCompanion
                  : [startRow, startCol],
                end: productCol === 0
                  ? [endRow, endCol]
                  : moveLeftCompanion,
                groupName: productCol === 0
                  ? `${moveRightCompanion.toString()}:${[endRow, endCol].toString()}`
                  : `${[startRow, startCol].toString()}:${moveLeftCompanion.toString()}`
              }
          }
        },
      },
      span: [Number(productRowSpan), Number(productColSpan), Number(productHeightSpan)],
      size: `${productRowSpan}x${productColSpan}`,
      choiceId,
      productType,
      fits: compatible || fitsBase,
      Sku
    });
  });

  const validProductSegments = _filter(productSegments, (productSegment) => productSegment.fits);
  return _uniqWith(validProductSegments, _isEqual);
});

/**
 * Determine the maximum row and column sizes for violating segments.
 * @param {Object} params - Parameters for determining violations.
 * @returns {Object} - { maxRowSize, maxColSize }.
 */
const getViolatingSegments = (
  hasViolatingSegmentHeights, isMultiStack, baseRowSpan, baseColSpan,
  topOnlyCoverage, selfAtTop, topItemsInRear, topItemsInFront,
  firstViolationGroup, lastViolationGroup
) => {
  // multistack
  if (isMultiStack) {

    // everything even
    if (!hasViolatingSegmentHeights) {
      if (topOnlyCoverage > ((baseRowSpan * baseColSpan) / 2)) {
        return {
          maxRowSize: 2,
          maxColSize: 2
        };
      }

      return {
        maxRowSize: 4,
        maxColSize: 4
      };
    }

    // nothing even
    if (hasViolatingSegmentHeights === 4 || hasViolatingSegmentHeights === 3) {
      return {
        maxRowSize: 2,
        maxColSize: 2
      };
    }

    // rear uneven, front uneven, both uneven
    if (hasViolatingSegmentHeights <= 2) {

      // self type at top of stack within multistack
      if (selfAtTop) {
        return {
          maxRowSize: 2,
          maxColSize: 4
        };
      }

      // violations in single stack within multistack
      if (firstViolationGroup.groupName === lastViolationGroup.companionGroupName) {

        // top only covering half of multistack
        if (topOnlyCoverage >= ((baseRowSpan * baseColSpan) / 2)) {

          if (topItemsInFront.length + topItemsInRear.length >= 3) {
            return {
              maxRowSize: 2,
              maxColSize: 2
            };
          }

          if (topItemsInFront.length === 2 || topItemsInRear.length === 2) {

            if (
              Number(firstViolationGroup?.groupName[0]) === topItemsInFront[0]?.Origin[0]
              || Number(lastViolationGroup?.groupName[0]) === topItemsInFront[0]?.Origin[0]
              || Number(firstViolationGroup?.groupName[0]) === topItemsInRear[0]?.Origin[0]
              || Number(lastViolationGroup?.groupName[0]) === topItemsInRear[0]?.Origin[0]
            ) {
              return {
                maxRowSize: 2,
                maxColSize: 4
              };
            }

            return {
              maxRowSize: 2,
              maxColSize: 2
            };
          }

          return {
            maxRowSize: 2,
            maxColSize: 2
          };
        }

        if (topItemsInFront.length === 1 || topItemsInRear.length === 1) {
          if (Number(firstViolationGroup?.groupName[0]) === topItemsInFront[0]?.Origin[0]
          || Number(lastViolationGroup?.groupName[0]) === topItemsInFront[0]?.Origin[0]
          || Number(firstViolationGroup?.groupName[0]) === topItemsInRear[0]?.Origin[0]
          || Number(lastViolationGroup?.groupName[0]) === topItemsInRear[0]?.Origin[0]) {

            return {
              maxRowSize: 2,
              maxColSize: 4
            };
          }

          return {
            maxRowSize: 2,
            maxColSize: 2
          };
        }

        return {
          maxRowSize: 2,
          maxColSize: 4
        };
      }

      // violation in single stack within multistack
      if (hasViolatingSegmentHeights === 1) {

        // top only covering half of multistack
        if (topOnlyCoverage >= ((baseRowSpan * baseColSpan) / 2)) {
          return {
            maxRowSize: 2,
            maxColSize: 2
          };
        }

        // at least 1 top item in rear or front
        if (topItemsInFront.length === 1 || topItemsInRear.length === 1) {
          return {
            maxRowSize: 2,
            maxColSize: 4,
          };
        }
      }

      if (firstViolationGroup === lastViolationGroup) {
        return {
          maxRowSize: 2,
          maxColSize: 4
        };
      }

      // violations in different stacks
      return {
        maxRowSize: 2,
        maxColSize: 2,
      };
    }
  }

  // violation in single stack
  if (hasViolatingSegmentHeights) {

    // crate at top of stack
    if (selfAtTop) {
      return {
        maxRowSize: 2,
        maxColSize: 4,
      };
    }

    if (topOnlyCoverage === ((baseRowSpan * baseColSpan) / 2)) {
      return {
        maxRowSize: 2,
        maxColSize: 2
      };
    }

    if (hasViolatingSegmentHeights === 2) {
      return {
        maxRowSize: 2,
        maxColSize: 2
      };
    }

    if (hasViolatingSegmentHeights === 1) {
      return {
        maxRowSize: 2,
        maxColSize: 2
      };
    }

    return {
      maxRowSize: 2,
      maxColSize: 4,
    };
  }

  if (topItemsInRear.length === 1) {
    return {
      maxRowSize: 2,
      maxColSize: 2
    };
  }

  // no violations in single stack
  return {
    maxRowSize: 2,
    maxColSize: 4
  };
};
const hasTopInclusiveChoice = (products, choiceIdsWithInclusive) => {
  return _some(products, (product) => _some(choiceIdsWithInclusive,
    (choice) => choice.id === product.choiceId && choice.productType === 'Top'));
};

/**
 * Determines which product choices should be enabled based on the current stack configuration.
 *
 * This function analyzes the existing stack of products and determines which additional products
 * (rear choices) can be added based on compatibility rules. It handles different scenarios for
 * wide and narrow products, considering their positions (left/right) and specific product metadata.
 *
 * @param {Array} stackProducts - Array of currently stacked products
 * @param {Object} baseItem - The base item/product configuration
 * @param {Object} choicesInfo - Object containing information about all available product choices
 * @param {Array<number>} arrayOfTopInclusive - Array of IDs representing choices that are exclusive to top products
 *
 * @returns {Array} enabledChoices - Array of product choice IDs that are compatible with the current stack
 */
const stackInclusiveChoices = (stackProducts, baseItem, choicesInfo, arrayOfTopInclusive) => {
  let enabledChoices = [];
  const lastStackItem = _last(stackProducts);
  if (!_isEmpty(stackProducts)) {
    if (lastStackItem?.type === 'wide') {
      const hasInclusive = lastStackItem.metadata?.InclusiveChoices;
      if (hasInclusive) {
        enabledChoices.push(...lastStackItem.metadata?.InclusiveChoices?.split(','));
      } else if (lastStackItem?.metadata?.ProductType !== 'Top') {
        // eslint-disable-next-line max-len
        enabledChoices.push(...Object.keys(choicesInfo).filter((choiceId) => !arrayOfTopInclusive.some((allowedId) => allowedId === parseInt(choiceId, 10))));
      }
    } else {
      // Rear Narrow Case
      const itemGroups = _groupBy(stackProducts, (item) => getSideName(item.Origin));
      const lastLeftItem = _last(itemGroups?.left);
      if (lastLeftItem) {
        const lastLeftInclusiveChoice = _last(itemGroups?.left)?.metadata?.InclusiveChoices;
        if (lastLeftInclusiveChoice) {
          enabledChoices.push(...lastLeftInclusiveChoice.split(','));
        } else if (lastLeftItem?.metadata?.ProductType !== 'Top') {
          const enabledRearArray = Object.keys(choicesInfo)
          // eslint-disable-next-line max-len
            .filter((choiceId) => choicesInfo[choiceId].visualizerType === 'narrow' && !arrayOfTopInclusive.some((allowedId) => allowedId === parseInt(choiceId, 10)))
            .map((choiceId) => parseInt(choiceId, 10));
          enabledChoices.push(...enabledRearArray);
        }
      }
      const lastRightItem = _last(itemGroups?.right)
        ? _last(itemGroups?.right) : _last(itemGroups?.left.filter((item) => item.type === 'wide'));
      if (lastRightItem) {
        const lastRightInclusiveChoices = _last(itemGroups?.right)?.metadata?.InclusiveChoices;
        if (lastRightInclusiveChoices) {
          enabledChoices.push(...lastRightInclusiveChoices.split(','));
        } else if (lastRightItem?.metadata?.ProductType !== 'Top') {
          const enabledRearArray = Object.keys(choicesInfo)
            // eslint-disable-next-line max-len
            .filter((choiceId) => choicesInfo[choiceId].visualizerType === 'narrow' && !arrayOfTopInclusive.some((allowedId) => allowedId === parseInt(choiceId, 10)))
            .map((choiceId) => parseInt(choiceId, 10));
          enabledChoices.push(...enabledRearArray);
        }
      }
      if ((!lastLeftItem || !lastRightItem) && baseItem?.metadata?.metadata?.InclusiveChoices) {
        enabledChoices.push(...baseItem.metadata?.metadata?.InclusiveChoices.split(','));
      } else if ((!lastLeftItem || !lastRightItem)) {
        const enabledFrontArray = Object.keys(choicesInfo)
          // eslint-disable-next-line max-len
          .filter((choiceId) => choicesInfo[choiceId].visualizerType === 'narrow' && !arrayOfTopInclusive.some((allowedId) => allowedId === parseInt(choiceId, 10)))
          .map((choiceId) => parseInt(choiceId, 10));
        enabledChoices.push(...enabledFrontArray);
      }

      const leftHeight = sumHeightsProducts(itemGroups?.left?.filter((item) => item.type === 'narrow'));
      const rightHeight = sumHeightsProducts(itemGroups?.right?.filter((item) => item.type === 'narrow'));

      // eslint-disable-next-line max-len
      if (leftHeight === rightHeight
          && ((!lastLeftItem?.metadata?.InclusiveChoices && !lastRightItem?.metadata?.InclusiveChoices)
          && (lastLeftItem?.metadata?.ProductType !== 'Top' && lastRightItem?.metadata?.ProductType !== 'Top'))) {
        const enabledRearArray = Object.keys(choicesInfo)
        // eslint-disable-next-line max-len
          .filter((choiceId) => choicesInfo[choiceId].visualizerType === 'wide' && !arrayOfTopInclusive.some((allowedId) => allowedId === parseInt(choiceId, 10)))
          .map((choiceId) => parseInt(choiceId, 10));
        enabledChoices.push(...enabledRearArray);
      }
      enabledChoices = new Set([...enabledChoices]);
    }
  } else if (baseItem?.metadata?.metadata?.InclusiveChoices) {
    enabledChoices.push(...baseItem.metadata?.metadata?.InclusiveChoices?.split(','));
  }

  if (enabledChoices.length === 0 && lastStackItem?.metadata?.ProductType !== 'Top') {
    // eslint-disable-next-line max-len
    enabledChoices.push(...Object.keys(choicesInfo).filter((choiceId) => !arrayOfTopInclusive.some((allowedId) => allowedId === parseInt(choiceId, 10))));
  }
  return enabledChoices;
};

/**
 * Filters the provided products based on inclusive choices defined in choicesInfo.
 *
 * @param {Array} products - The list of products to filter.
 * @param {Object} choicesInfo - An object containing choice information, where each key is a choice ID.
 * @returns {null|Array<number>|Array} - Returns:
 *   - `null` if no choicesInfo is provided or if no inclusive choices are defined.
 *   - An array of integers representing choice IDs to disable, based on inclusive choices.
 *   - `_initialState.disabled` if no specific conditions are met.
 */
const filterByInclusiveChoices = (products, choicesInfo, matrix, metadata) => {

  // Return early if no choicesInfo is provided
  if (!choicesInfo) {
    return null;
  }

  // Find all choices that have inclusive choices defined
  const choicesWithInclusive = _filter(choicesInfo, (choice) => choice?.inclusiveChoices);

  if (_isEmpty(choicesWithInclusive)) {
    return null;
  }

  const [baseRowSpan, baseColSpan] = getBaseRowColSpan(choicesInfo[_first(products)?.choiceId], matrix);
  const isMultiStack = (baseRowSpan === matrix?.rows && baseColSpan === matrix?.columns);

  const productGroups = splitIntoGroups(products, metadata, matrix);
  const baseItem = productGroups.Base?.items[0];

  const topInclusiveChoices = _filter(choicesWithInclusive, (choice) => choice.productType === 'Top');
  const arrayOfTopInclusive = _flatMap(topInclusiveChoices,
    (choice) => choice.inclusiveChoices?.split(',').map((id) => parseInt(id.trim(), 10)) || []
  );

  if (isMultiStack) {

    const lastChoiceId = _last(products)?.choiceId;
    const isLastChoiceInclusive = _some(choicesWithInclusive, (choice) => choice.id === lastChoiceId);
    // BASE CASE OF ONlY BASE PRODUCT WITH INCLUSIVE CHOICES
    if (_isEmpty(productGroups.front?.items) && _isEmpty(productGroups.rear?.items) && isLastChoiceInclusive) {
      const targetItem = _filter(choicesWithInclusive, (choice) => choice.id === lastChoiceId);
      const allowedChoiceIds = _flatMap(targetItem,
        (choice) => choice.inclusiveChoices?.split(',').map((id) => id.trim()) || []
      );
      return _map(
        _filter(Object.keys(choicesInfo),
          (choiceId) => !_some(allowedChoiceIds, (allowedId) => allowedId === choiceId)
        ),
        (choiceId) => parseInt(choiceId, 10)
      );
    }

    // Rear Stack
    const rearItems = productGroups?.rear?.items;
    const enabledRearChoices = stackInclusiveChoices(rearItems, baseItem, choicesInfo, arrayOfTopInclusive);

    // Front Stack
    const frontItems = productGroups?.front?.items;
    const enabledFrontChoices = stackInclusiveChoices(frontItems, baseItem, choicesInfo, arrayOfTopInclusive);

    const uniqueEnabledChoices = new Set([...enabledRearChoices, ...enabledFrontChoices]);
    const disableArray = Object.keys(choicesInfo)
      // eslint-disable-next-line max-len
      .filter((choiceId) => !Array.from(uniqueEnabledChoices).some((allowedId) => parseInt(allowedId, 10) === parseInt(choiceId, 10)))
      .map((choiceId) => parseInt(choiceId, 10));
    return disableArray;
  }

  // Single Stack
  const singleItems = productGroups?.single?.items;
  const enabledSingleChoices = stackInclusiveChoices(singleItems, baseItem, choicesInfo, arrayOfTopInclusive);

  const uniqueEnabledChoices = new Set([...enabledSingleChoices]);
  const disableArray = Object.keys(choicesInfo)
  // eslint-disable-next-line max-len
    .filter((choiceId) => !Array.from(uniqueEnabledChoices).some((allowedId) => parseInt(allowedId, 10) === parseInt(choiceId, 10)))
    .map((choiceId) => parseInt(choiceId, 10));
  return disableArray;
};

export {
  FALLBACK_CHOICE_INFO,
  filterByInclusiveChoices, getBaseRowColSpan, getViolatingSegments, mapChoiceSegments, sumArea, sumHeights
};
